import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Quote, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANDING } from "@/constants/testIds";

// 10 member testimonials — mixed layout. Photo cards show the actual profit
// screenshot alongside the quote; text-only cards render as a large pull-quote
// card with a prominent numeric result chip.
//
// SWAPPING IN NEW PROFIT SCREENSHOTS:
//   Change the item's `type` to "photo" and add an `imageUrl` (and `imageAlt`).
//   Everything else works automatically. The layout has 3 photo slots
//   reserved by design — currently Devon (real), plus Marcus & Sasha are
//   marked "// TODO: swap to photo when screenshot arrives".
const TESTIMONIALS = [
  {
    id: "devon",
    type: "photo",
    name: "Devon B.",
    roleKey: "testimonialsUi.devonRole",
    resultKey: "testimonialsUi.devonResult",
    quoteKey: "testimonialsUi.devonQuote",
    imageUrl: "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/2rh8klj4_IMG_1908.jpeg",
    imageAltKey: "testimonialsUi.devonImageAlt",
  },
  {
    id: "priya",
    type: "text",
    name: "Priya R.",
    roleKey: "testimonialsUi.priyaRole",
    resultKey: "testimonialsUi.priyaResult",
    quoteKey: "testimonialsUi.priyaQuote",
  },
  {
    id: "marcus",
    type: "photo",
    name: "Marcus T.",
    roleKey: "testimonialsUi.marcusRole",
    resultKey: "testimonialsUi.marcusResult",
    quoteKey: "testimonialsUi.marcusQuote",
    imageUrl: "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/vyac54le_IMG_8898.jpeg",
    imageAltKey: "testimonialsUi.marcusImageAlt",
  },
  {
    id: "carter",
    type: "text",
    name: "Carter M.",
    roleKey: "testimonialsUi.carterRole",
    resultKey: "testimonialsUi.carterResult",
    quoteKey: "testimonialsUi.carterQuote",
  },
  {
    id: "jordan",
    type: "photo",
    name: "Jordan V.",
    roleKey: "testimonialsUi.jordanRole",
    resultKey: "testimonialsUi.jordanResult",
    quoteKey: "testimonialsUi.jordanQuote",
    imageUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/gjdji624_IMG_1348.png",
    imageAltKey: "testimonialsUi.jordanImageAlt",
  },
  {
    id: "dario",
    type: "text",
    name: "Dario S.",
    roleKey: "testimonialsUi.darioRole",
    resultKey: "testimonialsUi.darioResult",
    quoteKey: "testimonialsUi.darioQuote",
  },
  {
    id: "sasha",
    type: "photo",
    name: "Sasha K.",
    roleKey: "testimonialsUi.sashaRole",
    resultKey: "testimonialsUi.sashaResult",
    quoteKey: "testimonialsUi.sashaQuote",
    imageUrl: "https://customer-assets.emergentagent.com/job_wealth-academy-46/artifacts/at1d2fsy_IMG_8897.jpeg",
    imageAltKey: "testimonialsUi.sashaImageAlt",
  },
  {
    id: "amelia",
    type: "text",
    name: "Amelia H.",
    roleKey: "testimonialsUi.ameliaRole",
    resultKey: "testimonialsUi.ameliaResult",
    quoteKey: "testimonialsUi.ameliaQuote",
  },
  {
    id: "rania",
    type: "photo",
    name: "Rania E.",
    roleKey: "testimonialsUi.raniaRole",
    resultKey: "testimonialsUi.raniaResult",
    quoteKey: "testimonialsUi.raniaQuote",
    imageUrl: "https://customer-assets-7cd3h4nn.emergentagent.net/job_wealth-academy-46/artifacts/f9ud07g3_IMG_4285.webp",
    imageAltKey: "testimonialsUi.raniaImageAlt",
  },
  {
    id: "ines",
    type: "text",
    name: "Ines M.",
    roleKey: "testimonialsUi.inesRole",
    resultKey: "testimonialsUi.inesResult",
    quoteKey: "testimonialsUi.inesQuote",
  },
];

function ResultChip({ children, dark = false }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold ${
        dark
          ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
          : "bg-amber-500/10 text-amber-700 border border-amber-500/30"
      }`}
    >
      <TrendingUp size={12} /> {children}
    </div>
  );
}

function PhotoCard({ t, tFunc }) {
  return (
    <article className="grid grid-cols-1 md:grid-cols-5 bg-white border border-blue-900/10 rounded-2xl overflow-hidden shadow-sm h-full">
      <div className="md:col-span-2 relative aspect-[3/4] md:aspect-auto md:min-h-[560px] bg-slate-950">
        <img
          src={t.imageUrl}
          alt={tFunc(t.imageAltKey)}
          className="absolute inset-0 w-full h-full object-contain"
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-950/80 to-transparent md:hidden" />
        <div className="absolute bottom-4 left-4 right-4 md:hidden">
          <ResultChip dark>{tFunc(t.resultKey)}</ResultChip>
        </div>
      </div>
      <div className="md:col-span-3 p-8 sm:p-10 flex flex-col">
        <Quote className="text-amber-500" size={28} />
        <blockquote className="mt-4 font-serif-quote text-xl sm:text-2xl text-slate-900 leading-[1.4]">
          {tFunc(t.quoteKey)}
        </blockquote>
        <div className="mt-auto pt-8 border-t border-slate-100 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold text-slate-900">{t.name}</div>
            <div className="text-sm text-slate-600">{tFunc(t.roleKey)}</div>
          </div>
          <div className="hidden md:block">
            <ResultChip>{tFunc(t.resultKey)}</ResultChip>
          </div>
        </div>
      </div>
    </article>
  );
}

function TextCard({ t, tFunc }) {
  return (
    <article className="bg-white border border-blue-900/10 rounded-2xl overflow-hidden shadow-sm h-full">
      <div className="p-8 sm:p-12 flex flex-col h-full">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <Quote className="text-amber-500" size={36} />
          <ResultChip>{tFunc(t.resultKey)}</ResultChip>
        </div>
        <blockquote className="mt-6 font-serif-quote text-2xl sm:text-3xl text-slate-900 leading-[1.35] max-w-3xl">
          {tFunc(t.quoteKey)}
        </blockquote>
        <div className="mt-auto pt-10 border-t border-slate-100 flex items-center gap-4">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-800 to-blue-950 text-white flex items-center justify-center font-display font-bold text-base">
            {t.name.split(" ").map((s) => s[0]).join("")}
          </div>
          <div>
            <div className="font-semibold text-slate-900">{t.name}</div>
            <div className="text-sm text-slate-600">{tFunc(t.roleKey)}</div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function TestimonialCarousel() {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const interval = setInterval(() => emblaApi.scrollNext(), 8000);
    return () => {
      emblaApi.off("select", onSelect);
      clearInterval(interval);
    };
  }, [emblaApi]);

  return (
    <section
      data-testid={LANDING.testimonialCarousel}
      className="bg-blue-50/60 border-y border-blue-900/5 py-24 relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-3">
              {t("testimonialsUi.eyebrow")}
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tighter leading-tight">
              {t("testimonialsUi.heading")}
            </h2>
            <p className="mt-3 text-slate-600 text-sm max-w-xl">
              {t("testimonialsUi.subheading")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={scrollPrev}
              data-testid={LANDING.testimonialPrev}
              aria-label={t("testimonialsUi.prevAriaLabel")}
              className="w-10 h-10 rounded-full border border-slate-300 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors flex items-center justify-center text-slate-700"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              data-testid={LANDING.testimonialNext}
              aria-label={t("testimonialsUi.nextAriaLabel")}
              className="w-10 h-10 rounded-full border border-slate-300 bg-white hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors flex items-center justify-center text-slate-700"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden -mx-2" ref={emblaRef}>
          <div className="flex touch-pan-y items-stretch">
            {TESTIMONIALS.map((item) => (
              <div
                key={item.id}
                data-testid={LANDING.testimonialSlide}
                data-testimonial-type={item.type}
                className="flex-[0_0_100%] min-w-0 px-2"
              >
                {item.type === "photo" ? <PhotoCard t={item} tFunc={t} /> : <TextCard t={item} tFunc={t} />}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2">
          {TESTIMONIALS.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => scrollTo(i)}
              data-testid={LANDING.testimonialDot}
              aria-label={t("testimonialsUi.dotAriaLabel", { number: i + 1 })}
              className={`h-1.5 rounded-full transition-all ${
                selectedIndex === i
                  ? "bg-slate-900 w-8"
                  : "bg-slate-300 hover:bg-slate-500 w-3"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
